import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Star, Car, User as UserIcon, Loader2 } from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const StarPicker = ({ value, onChange, label, testId }) => {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="space-y-2" data-testid={testId}>
      <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
      <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? 0 : n)}
            onMouseEnter={() => setHover(n)}
            className="p-1 transition-transform hover:scale-110"
            data-testid={`${testId}-star-${n}`}
          >
            <Star className={`h-7 w-7 ${n <= active ? 'fill-orange-400 text-orange-400' : 'text-gray-600'}`} />
          </button>
        ))}
        {value > 0 && (
          <button type="button" onClick={() => onChange(0)} className="text-[10px] text-gray-500 ml-2 self-end pb-2 hover:text-gray-300">
            ×&nbsp;törlés
          </button>
        )}
      </div>
    </div>
  );
};

const ReviewPromptModal = () => {
  const { user, api } = useAuth();
  const { toast } = useToast();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [carRating, setCarRating] = useState(0);
  const [landlordRating, setLandlordRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  // Fetch pending reviews once per session, after user is loaded
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/reviews/my-pending');
        if (cancelled) return;
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
          setPending(data);
          setOpen(true);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user, api]);

  if (!pending.length) return null;
  const current = pending[0];
  const hasAny = carRating > 0 || landlordRating > 0 || comment.trim().length > 0;

  const reset = () => {
    setCarRating(0); setLandlordRating(0); setComment('');
  };

  const handleNext = (newList) => {
    reset();
    if (newList.length === 0) setOpen(false);
    setPending(newList);
  };

  const submit = async () => {
    if (!hasAny) {
      toast({ variant: "destructive", title: "Adj meg legalább 1 csillagot vagy írj pár szót!" });
      return;
    }
    setBusy(true);
    try {
      const body = { rental_id: current._id };
      if (carRating > 0) body.rating = carRating;
      if (landlordRating > 0) body.landlord_rating = landlordRating;
      if (comment.trim()) body.comment = comment.trim();
      await api.post('/reviews/', body);
      toast({ title: "Köszönjük az értékelést!" });
      handleNext(pending.slice(1));
    } catch (e) {
      toast({ variant: "destructive", title: "Hiba", description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/dismiss/${current._id}`);
      handleNext(pending.slice(1));
    } catch (e) {
      handleNext(pending.slice(1));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) dismiss(); }}>
      <DialogContent className="bg-black/95 border-orange-500/30 max-w-md text-white" data-testid="review-prompt-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-orange-400 flex items-center gap-2">
            <Star className="h-5 w-5 fill-orange-400" /> Értékeld a bérlésed
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Hogy tetszett <span className="text-orange-300 font-semibold">{current.car_name || 'a jármű'}</span>?
            {current.landlord_name && (
              <span className="block text-xs mt-1">Bérbeadó: <span className="text-white">{current.landlord_name}</span></span>
            )}
            <span className="block text-[11px] text-gray-500 mt-1">Bérlés: {current.start_time} → {current.end_time}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3">
            <StarPicker value={carRating} onChange={setCarRating} label={<><Car className="inline h-3 w-3 mr-1" /> Az autó értékelése</>} testId="car-rating" />
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3">
            <StarPicker value={landlordRating} onChange={setLandlordRating} label={<><UserIcon className="inline h-3 w-3 mr-1" /> A bérbeadó értékelése</>} testId="landlord-rating" />
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Vélemény (opcionális)</p>
            <Textarea
              value={comment} onChange={e => setComment(e.target.value)}
              placeholder="Mit szeretnél megosztani a bérlésről? (max 500 karakter)"
              className="bg-black/60 border-white/10 text-white resize-none h-20"
              maxLength={500}
              data-testid="review-comment"
            />
            <p className="text-[10px] text-gray-600 text-right mt-1">{comment.length} / 500</p>
          </div>

          {pending.length > 1 && (
            <p className="text-[11px] text-gray-500 text-center">
              + még {pending.length - 1} bérlés vár értékelésre
            </p>
          )}
        </div>

        <div className="flex justify-between items-center gap-2 pt-2">
          <Button variant="ghost" onClick={dismiss} disabled={busy} className="text-gray-500 hover:text-gray-300 text-xs" data-testid="review-dismiss-btn">
            Nem értékelem (örökre)
          </Button>
          <Button onClick={submit} disabled={busy || !hasAny} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="review-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Star className="h-4 w-4 mr-2" />}
            Küldés
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewPromptModal;
